import { FC } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import { Asset, ProductWithSides } from 'shared-types';
import styled from 'styled-components';

import useLayoutStore from 'hooks/stores/useLayoutStore';
import useAssetPreview from 'hooks/useAssetPreview';

interface Props {
  sideId: ProductWithSides['sides'][number]['id'];
  assetId: Asset['id'];
  style?: React.CSSProperties;
  initialUrl?: string;
}

export const SPreviewImage = styled.div`
  box-shadow: 0px 4px 8px -2px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 1px;
  margin-bottom: 4px;
  height: 100px;
  min-width: 75px;
  max-width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    height: 70px;
    min-width: 55px;
    max-width: 60px;
    margin-bottom: 2px;
  }
`;

const PreviewImage: FC<Props> = ({ assetId, sideId, style, initialUrl }) => {
  const { imageUrl, isLoading, hasError, onImageLoad, onImageError } = useAssetPreview({
    assetId,
    sideId,
    initialUrl,
  });

  const layoutStore = useLayoutStore((state) => state);

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, assetId: Asset['id']) => {
    event.dataTransfer.setData('assetId', assetId);
    layoutStore.setShouldHighlightDropArea(true);
  };

  const renderLoader = () => {
    // Voor nu: gewone ClipLoader voor alle loading states
    return <ClipLoader color="#35b3ee" />;

    // Later kunnen we hier verschillende loaders per loadingState tonen:
    // switch (loadingState) {
    //   case 'action-loading':
    //     return <ActionSpinner text="Verwerken..." />;
    //   case 'processing':
    //     return <ProcessingLoader text="Server verwerkt..." />;
    //   case 'image-loading':
    //     return <ImageLoader text="Laden..." />;
    //   default:
    //     return <ClipLoader color="#35b3ee" />;
    // }
  };

  const renderContent = () => {
    if (hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#666',
            textAlign: 'center',
          }}
        >
          Fout bij laden
        </div>
      );
    }

    return (
      <>
        {/* Always render img so onLoad can fire, even if loading */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Asset preview"
            loading="lazy"
            onLoad={onImageLoad}
            onError={onImageError}
            style={{
              opacity: isLoading ? 0 : 1,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
        )}
        {/* Show loader over the image */}
        {isLoading && renderLoader()}
      </>
    );
  };

  return (
    <SPreviewImage
      onDragStart={(e) => onDragStart(e, assetId)}
      style={style}
      draggable={!isLoading && !hasError}
    >
      {renderContent()}
    </SPreviewImage>
  );
};

export default PreviewImage;
